<template>
  <div class="page-content">
    <div class="headings">
      <h2 class="title title-primary">
        {{ location }}
      </h2>
      <span>|</span>
      <h2 class="title title-primary">
        {{ $t("lang.inventory.detail.page_title_line_1") }}
      </h2>
      <span>|</span>
      <h3 class="title title-primary">
        {{ $t("lang.inventory.detail.page_title_line_2") }}
      </h3>
    </div>
    <div class="page-header">
      <div class="boxes" v-if="!shouldLoading">
        <bx-box
          dark
          color="outline"
          class="col-12 col-md-auto p-1"
          :title="$t('lang.inventory.detail.box_title_1')"
        >
          <template slot="actions">
            <bx-inventory-status
              v-if="inventory.canceled"
              :canceled="inventory.canceled"
            ></bx-inventory-status>
            <bx-inventory-status
              v-else
              :status="inventory.valid"
            ></bx-inventory-status>
          </template>
          <div class="detail-titles">
            <div class="wrapper">
              <h4>
                Nº
                <strong>{{ inventory.id }}</strong>
              </h4>
              <h5>
                <strong>{{ inventory.start_time }}</strong>
              </h5>
            </div>
          </div>
        </bx-box>

        <bx-box
          dark
          :title="$t('lang.inventory.detail.box_title_2')"
          no-padding
          class="col-12 col-md-auto p-1"
        >
          <bx-complementary
            v-if="inventory.items"
            :total="Math.abs(inventory.theorical_stock)"
            :total-icon="require('@/assets/i/orderWhite.png')"
            :left="{ value: inventory.items }"
            :right="{ value: inventory.theorical_stock - inventory.items }"
          />
        </bx-box>
        <div class="col-12 col-md-3 col-lg-3 m-0">
          <bx-btn
            class="col-lg-12 bx-tag bx-uppercase rounded-pill btn-validation"
            style="padding: 12px 12px 12px 8px;  margin-top:15px"
            color="green"
            block
            v-if="
              !shouldLoading &&
                !validationMode &&
                !inventory.valid &&
                !inventory.canceled
            "
            @click="onClick('sendValidation')"
            >{{ $t("lang.inventory.detail.btn_validation_pending") }}</bx-btn
          >
          <span
            class="inventory-status"
            block
            v-else-if="!shouldLoading && !validationMode && inventory.valid"
          >
            <div
              class="col-lg-11 bx-tag rounded-pill bx-bg-color-green bx-uppercase text-center btn-validation"
              style="padding: 12px 12px 12px 8px;  margin-top:15px"
            >
              {{ $t("lang.inventory.detail.btn_validation_done") }}
            </div>
          </span>
        </div>

        <div class="col-12 col-md-3 col-lg-3 m-0">
          <bx-btn
            class="col-lg-12 bx-tag bx-uppercase rounded-pill btn-validation"
            style="padding: 12px 12px 12px 8px;  margin-top:15px"
            color="green"
            block
            v-if="
              !shouldLoading &&
                !validationMode &&
                !inventory.valid &&
                !inventory.canceled
            "
            @click="onClick('sendUnvalidation')"
            >{{ $t("lang.inventory.detail.btn_validation_cancel") }}</bx-btn
          >
        </div>
      </div>
      <skeleton-boxes progress-box :boxes="2" v-else />
    </div>

    <div class="page-main-content">
      <div class="container-fluid">
        <div class="row" style="margin: 8px">
          <bx-btn @click="$router.back()">{{
            $t("lang.messages.go_back")
          }}</bx-btn>
        </div>
        <v-row class="d-flex d-sm-none">
          <v-list width="100%">
            <v-list-item
              two-line
              v-for="(item, index) in data"
              :key="index"
              class="green lighten-5 ma-2 rounded-lg"
              ripple
            >
              <v-list-item-content>
                <v-list-item-title>
                  <div>
                    {{ item.gtin }}
                  </div>
                </v-list-item-title>
                <v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
                <v-list-item-subtitle class="text-caption">{{
                  item.lot
                }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-list-item-subtitle class="text-caption">
                  {{ item.quantity }}
                </v-list-item-subtitle>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-row>
        <div class="row">
          <div class="col-12">
            <bx-box
              :title="`${data.length} Stock de <strong>GTINs</strong>`"
              no-padding
              :loading="shouldLoading"
            >
              <skeleton-table :columns="9" v-if="shouldLoading" />
              <bx-table
                v-if="!shouldLoading"
                :data="data"
                :columns="columns"
                paginated
                backendPagination
                :currentPage="page"
                @search="onSearch"
                :total="ordersCount"
                :perPage="itemsPerPage"
                @update:currentPage="updatePage"
              >
                <template slot-scope="props">
                  <bx-table-column field="gtin">{{
                    props.row.epc
                  }}</bx-table-column>
                  <bx-table-column field="gtin">{{
                    props.row.gtin
                  }}</bx-table-column>
                  <bx-table-column field="section" centered>{{
                    props.row.description
                  }}</bx-table-column>
                  <bx-table-column field="season" centered>{{
                    props.row.lot
                  }}</bx-table-column>
                  <bx-table-column field="section" centered>{{
                    props.row.quantity
                  }}</bx-table-column>
                </template>
              </bx-table>
            </bx-box>
          </div>
        </div>
      </div>
    </div>

    <v-dialog v-model="confirmDialog" persistent scrollable width="600px">
      <v-card>
        <v-card-title>
          <h4 class="mt-3 w-100 text-left text-capitalize">
            {{
              mode === "sendValidation"
                ? $t("lang.inventory.detail.confirm_validation")
                : $t("lang.inventory.detail.confirm_cancelation")
            }}
          </h4>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="mr-10" color="#9bc31c" @click="onCancel" dark>
            No
          </v-btn>
          <v-btn class="mr-5" color="#9bc31c" @click="onConfirmation" dark>
            {{ $t("lang.inventory.detail.yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import BxComplementaryMini from "../../bx-components/BxComplementaryMini";
import BxInventoryStatus from "../../bx-components/BxInventoryStatus";
import BxBtn from "../../bx-components/BxBtn";

export default {
  name: "inbound-dashboard",
  components: { BxComplementaryMini, BxInventoryStatus, BxBtn },
  props: {
    id: {
      type: [Number, String]
    }
  },
  data() {
    return {
      key: "inventory_detail",
      shouldLoading: true,
      columns: [],
      isdark: true,
      data: [],
      ordersCount: 1,
      inventory: {},
      validationMode: false,
      page: 1,
      itemsPerPage: process.env.VUE_APP_ITEMS_PER_PAGE,
      data_users: [],
      filters: [],
      confirmDialog: false,
      mode: ""
    };
  },
  computed: {
    location() {
      return localStorage.getItem("location");
    }
  },
  methods: {
    onSearch(field, value) {
      this.$session.set(this.key + field, value);
      this.filters[field] = value;
      this.getData();
    },
    updatePage(p) {
      this.page = p;
      this.getData();
    },
    async getData() {
      this.inventory = await this.$api.inventories.get(this.id);
      this.data_users = await this.$api.inventories.users(this.id);
      const data = await this.$api.inventories.summary_items(this.id, {
        itemType__gtin__contains: this.filters.gtin,
        offset: (this.page - 1) * this.itemsPerPage,
        limit: this.itemsPerPage
      });
      this.ordersCount = data.count;
      this.data = data.results.map(item => {
        return {
          gtin: item.gtin,
          theorical_quantity: item.theorical_quantity,
          stockRatingPercent:
            item.theorical_quantity < item.quantity
              ? (item.theorical_quantity / item.quantity) * 100
              : (item.quantity / item.theorical_quantity) * 100,
          ismore: item.theorical_quantity < item.quantity,
          quantity: item.quantity,
          lot: item.lot,
          model: item.model,
          epc: item.epc,
          description: item.description,
        };
      });

      this.columns = [
        {
          field: "epc",
          label: this.$t("lang.epc.epc")
        },
        {
          field: "gtin",
          label: this.$t("lang.inbound.order_detail.table_field_1")
        },
        {
          field: "description",
          label: this.$t("lang.inbound.order_detail.description")
        },
        {
          field: "lot",
          label: this.$t("lang.inbound.order_detail.lot")
        },
        {
          field: "quantity",
          label: this.$t("lang.inbound.order_detail.quantity")
        },
      ];

      this.shouldLoading = false;
    },
    onClick(mode) {
      this.mode = mode;
      this.confirmDialog = true;
    },
    onConfirmation() {
      if (this.mode === "sendUnvalidation") {
        this.sendUnvalidation();
      } else {
        this.sendValidation();
      }
    },
    onCancel() {
      this.confirmDialog = false;
    },
    sendValidation() {
      this.$api.inventories.validate(this.id).then(res => {
        this.validationMode = true;
        this.getData();
        this.onCancel();
      });
    },
    cancelValidation() {
      this.validationMode = false;
      this.getData();
    },
    sendUnvalidation() {
      this.$api.inventories.unvalidate(this.id).then(res => {
        this.validationMode = true;
        this.getData();
        this.onCancel();
      });
    }
  },
  mounted() {
    this.getData();
    this.filters.gtin = this.$session.get(this.key + "gtin");
  }
};
</script>
<style scoped>
@media screen and (min-width: 768px) {
  .btn-validation {
    width: 130px;
    float: right;
  }
}
</style>
